// src/axiosInstance.js
import axios from 'axios';
import { ensureMentoringSession } from './lib/session';
import ensureAuthSession from './utils/ensureAuthSession';

/* ========================= Utils ========================= */

const stripTrailingSlashes = (url) => (url || '').replace(/\/+$/, '');
const ensureLeadingSlash = (path = '') =>
  path && !path.startsWith('/') ? `/${path}` : (path || '');
const escapeRegExp = (s = '') => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const baseHasSegment = (baseUrl, seg) => {
  if (!seg) return false;
  try {
    const u = new URL(baseUrl, window.location?.origin || 'http://localhost');
    const p = (u.pathname || '').replace(/\/+$/, '');
    const rx = new RegExp(`(^|/)${escapeRegExp(seg)}(/|$)`, 'i');
    return rx.test(p);
  } catch {
    const p = String(baseUrl || '').replace(/^[^/]*:\/\/[^/]+/, '').replace(/\/+$/, '');
    const rx = new RegExp(`(^|/)${escapeRegExp(seg)}(/|$)`, 'i');
    return rx.test(p);
  }
};

const dropLeadingSegment = (path = '', seg = '') => {
  if (!path || !seg) return ensureLeadingSlash(path);
  const rx = new RegExp(`^\\/?${escapeRegExp(seg)}\\/?`, 'i');
  const cleaned = (path || '').replace(rx, '/');
  return ensureLeadingSlash(cleaned);
};

const getCookie = (name) => {
  const row = document.cookie.split('; ').find(c => c.startsWith(name + '='));
  return row ? decodeURIComponent(row.split('=').slice(1).join('=')) : null;
};

/* ========================= Base URLs ========================= */

export const AUTH_API = stripTrailingSlashes(
  process.env.REACT_APP_AUTH_API_BASE ||
  process.env.REACT_APP_AUTH_API ||
  'https://localhost:3000'
);

export const MENTORING_API = stripTrailingSlashes(
  process.env.REACT_APP_MENTORING_API_BASE ||
  process.env.REACT_APP_MENTORING_API ||
  'https://localhost:3006'
);

export const SURVEY_API = stripTrailingSlashes(
  process.env.REACT_APP_SURVEY_API_BASE ||
  process.env.REACT_APP_SURVEY_API ||
  '/survey' // <<<< Anpassung: Default auf Same-Origin Route
);

/* ========================= Axios Defaults ========================= */

const COMMON_HEADERS = {
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};
const DEFAULT_TIMEOUT = 15000;

const createApi = (baseUrl, baseSegment) => {
  const instance = axios.create({
    baseURL: baseUrl,
    withCredentials: true,                 // <<<< Cookies immer mitsenden
    timeout: DEFAULT_TIMEOUT,
    headers: { ...COMMON_HEADERS },
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-CSRF-Token',
  });

  const shouldDrop = baseHasSegment(baseUrl, baseSegment);

  instance.interceptors.request.use((config) => {
    if (typeof config.url === 'string') {
      if (shouldDrop) {
        config.url = dropLeadingSegment(config.url, baseSegment);
      }
      config.url = ensureLeadingSlash(config.url);
    }

    const xsrf = getCookie('XSRF-TOKEN');
    if (xsrf) {
      config.headers = config.headers || {};
      if (!config.headers['X-CSRF-Token']) config.headers['X-CSRF-Token'] = xsrf;
      if (!config.headers['X-XSRF-TOKEN']) config.headers['X-XSRF-TOKEN'] = xsrf;
    }

    config.withCredentials = true;
    return config;
  });

  return instance;
};

/* ========================= API Instanzen ========================= */

export const authApi      = createApi(AUTH_API, 'auth');
export const mentoringApi = createApi(MENTORING_API, 'mentoring');
export const surveyApi    = createApi(SURVEY_API, 'survey');

/* ========================= AUTH 401 → ensureAuthSession → Retry ========================= */

let reauthInFlight = null; // verhindert Parallel-Stürme

authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error?.config || {};
    const status = error?.response?.status;

    // Nur einmal retryen, nur bei 401 vom authApi
    if (status === 401 && !cfg.__retried) {
      try {
        if (!reauthInFlight) {
          reauthInFlight = ensureAuthSession().finally(() => { reauthInFlight = null; });
        }
        await reauthInFlight;

        cfg.__retried = true;
        cfg.withCredentials = true;

        // erneuter Versuch über das gleiche Instance (Request-Interceptor setzt CSRF)
        return authApi.request(cfg);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

/* ========================= Legacy Default-Client ========================= */

const defaultApi = axios.create({
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT,
  headers: { ...COMMON_HEADERS },
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-CSRF-Token',
});

defaultApi.interceptors.request.use((config) => {
  if (typeof config.url === 'string') {
    config.url = ensureLeadingSlash(config.url);
  }
  const xsrf = getCookie('XSRF-TOKEN');
  if (xsrf) {
    config.headers = config.headers || {};
    if (!config.headers['X-CSRF-Token']) config.headers['X-CSRF-Token'] = xsrf;
    if (!config.headers['X-XSRF-TOKEN']) config.headers['X-XSRF-TOKEN'] = xsrf;
  }
  config.withCredentials = true;
  return config;
});

/* ========================= Preflight: Mentoring ready =========================
   Blockiert ALLE /mentoring-Requests, bis eine valide Session existiert.
   Verwendet fetch(), um Interceptor-Rekursion zu vermeiden. */

let mentoringReady = false;
let mentoringReadyPromise = null;

async function ensureMentoringReadyOnce() {
  if (mentoringReady) return;

  // 1) schneller Check: /mentoring/whoami
  try {
    const r = await fetch('/mentoring/whoami', { credentials: 'include' });
    if (r.ok) {
      const j = await r.json().catch(() => ({}));
      if (j && (j.ok || j.user)) {
        mentoringReady = true;
        return;
      }
    }
  } catch {}

  // 2) „weicher“ Weg (nutzt ggf. deine vorhandene Session-Logik)
  try {
    await ensureMentoringSession();
    const r = await fetch('/mentoring/whoami', { credentials: 'include' });
    if (r.ok) {
      const j = await r.json().catch(() => ({}));
      if (j && (j.ok || j.user)) {
        mentoringReady = true;
        return;
      }
    }
  } catch {}

  // 3) Harte Kopplung via /auth/whoami -> /mentoring/sso-attach
  try {
    const wr = await fetch('/auth/whoami', { credentials: 'include' });
    const who = wr.ok ? await wr.json() : null;
    const u = who?.user ?? null;
    // << Änderung: nicht mehr streng 'isAuthenticated' prüfen, sondern vorhandene E-Mail
    if (u?.email) {
      const payload = {
        user: {
          id:        u.id ?? u.user_id ?? null,
          user_id:   u.user_id ?? u.id ?? null,
          email:     u.email ?? null,
          role:      u.role ?? null,
          firstName: u.firstName ?? u.first_name ?? null,
          lastName:  u.lastName ?? u.last_name ?? null,
          client_id: u.client_id ?? u.customer_id ?? u.clientId ?? null,
          customer_id: u.customer_id ?? u.client_id ?? u.customerId ?? null,
        }
      };
      await fetch('/mentoring/sso-attach', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // verifizieren
      const vr = await fetch('/mentoring/whoami', { credentials: 'include' });
      if (vr.ok) {
        const j = await vr.json().catch(() => ({}));
        if (j && (j.ok || j.user)) {
          mentoringReady = true;
          return;
        }
      }
    }
  } catch {}

  // notfalls: weiterlaufen – Response-Interceptor fängt 401/403 weiterhin ab
}

const addMentoringPreflight = (instance) => {
  instance.interceptors.request.use(async (config) => {
    // Nur für echte Mentoring-Calls
    const url = String(config.url || '');
    const base = String(config.baseURL || '');
    const isMentoring =
      url.startsWith('/mentoring') || /(^|\/)mentoring(\/|$)/i.test(base);

    if (isMentoring) {
      if (!mentoringReady) {
        if (!mentoringReadyPromise) {
          mentoringReadyPromise = ensureMentoringReadyOnce()
            .catch(() => {})
            .finally(() => { mentoringReadyPromise = null; });
        }
        await mentoringReadyPromise;
      }
    }
    return config;
  });
};

/* ========================= 401/403 → attach → Retry ========================= */

const isMentoringRequest = (cfg = {}) => {
  const url = cfg.url || '';
  const base = cfg.baseURL || '';
  return url.startsWith('/mentoring') || /(^|\/)mentoring(\/|$)/i.test(base);
};

const addMentoringRetry = (instance) => {
  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const { response, config } = error || {};
      if (!response || !config) return Promise.reject(error);

      const status = response.status;
      const looksMentoring = isMentoringRequest(config);

      if ((status === 401 || status === 403) && looksMentoring && !config.__retryAfterAttach) {
        try {
          await ensureMentoringSession();
          config.__retryAfterAttach = true;
          return instance.request(config);
        } catch {
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    }
  );
};

// Preflight & Retry aktivieren
addMentoringPreflight(mentoringApi);
addMentoringPreflight(defaultApi);
addMentoringRetry(mentoringApi);
addMentoringRetry(defaultApi);

/* ========================= CSRF Helper ========================= */

export const refreshCsrfToken = async () => {
  try {
    await authApi.get('/csrf-token', { withCredentials: true, timeout: 8000 });
  } catch {
    try { await authApi.get('/whoami',  { withCredentials: true, timeout: 8000 }); } catch {}
    try { await authApi.get('/profile', { withCredentials: true, timeout: 8000 }); } catch {}
  }
  return getCookie('XSRF-TOKEN');
};

/* ========================= Exporte ========================= */

export default defaultApi;
